import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import VectorSource from 'ol/source/Vector';
import WKT from 'ol/format/WKT';
import SymbolLayer from './Layers/SymbolLayer';
import MaskLayer from './Layers/MaskLayer';
import HeatmapLayer from './Layers/HeatmapLayer';
import ProtomapsLayer from './Layers/ProtomapsLayer';
import DtmLayer from './Layers/DtmLayer';
import isEqual from 'lodash/isEqual';

const LayerManager = ({ map, layers = [], mapOptions, mapSettings, selectedPillarKey, symbolLayerZIndex = 1000, fontVersion = 0 }) => {
  const [layerInstances, setLayerInstances] = useState(new Map());
  const previousLayersRef = useRef([]);
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current++;
  });

  // Function to create a layer instance
  const createLayer = useCallback((layer) => {
    if (!layer || !layer.type) return null;
    
    if (layer.type === 'symbol') {
      return (
        <SymbolLayer
          key={layer.id}
          map={map}
          data={layer.data}
          symbol={layer.symbol}
          showLabels={layer.showLabels}
          labelColumn={layer.labelColumn}
          zIndex={layer.zIndex || symbolLayerZIndex}
          opacity={layer.opacity}
          scale={layer.scale}
          scaleType={layer.scaleType}
          scaleColumn={layer.scaleColumn}
          visible={layer.visible !== false}
          showInLegend={layer.showInLegend}
          declutter={layer.declutter !== false}
          style={layer.style}
          fontVersion={fontVersion}
        />
      );
    }
    
    return null;
  }, [map, symbolLayerZIndex, fontVersion]);

  // Memoized function to check if layer needs update
  const shouldUpdateLayer = useCallback((newLayer, oldLayer) => {
    if (!oldLayer) return true;
    
    // Fast ID check first
    if (newLayer.id !== oldLayer.id) return true;
    
    // Compare only essential properties that would affect rendering
    const essentialProps = {
      data: newLayer.data,
      style: newLayer.style,
      visible: newLayer.visible,
      opacity: newLayer.opacity,
      zIndex: newLayer.zIndex,
      showLabels: newLayer.showLabels,
      scale: newLayer.scale
    };
    
    const oldEssentialProps = {
      data: oldLayer.data,
      style: oldLayer.style,
      visible: oldLayer.visible,
      opacity: oldLayer.opacity,
      zIndex: oldLayer.zIndex,
      showLabels: oldLayer.showLabels,
      scale: oldLayer.scale
    };
    
    return !isEqual(essentialProps, oldEssentialProps);
  }, []);

  // Effect to manage layer instances
  useEffect(() => {
    if (!map || !layers) return;

    let hasChanges = false;
    const newLayerInstances = new Map(layerInstances);

    // Process each layer
    layers.forEach(layer => {
      const oldLayer = previousLayersRef.current.find(l => l.id === layer.id);
      
      if (shouldUpdateLayer(layer, oldLayer)) {
        const newLayerInstance = createLayer(layer);
        if (newLayerInstance) {
          newLayerInstances.set(layer.id, newLayerInstance);
          hasChanges = true;
        }
      }
    });

    // Remove layers that no longer exist
    layerInstances.forEach((_, layerId) => {
      if (!layers.find(l => l.id === layerId)) {
        newLayerInstances.delete(layerId);
        hasChanges = true;
      }
    });

    // Only update state if there are actual changes
    if (hasChanges) {
      setLayerInstances(newLayerInstances);
      previousLayersRef.current = layers;
      
      // Force map render only when layers actually change
      if (map) {
        map.render();
      }
    }
  }, [map, layers, createLayer, shouldUpdateLayer]);

  const heatmapLayers = useMemo(() => {
    if (!map || !layers || layers.length < 1) return null;

    return layers
      .filter(layer => layer.type === 'heatmap')
      .map((d, index) => (
        <HeatmapLayer
          key={`${d.id}-${index}`}
          map={map}
          data={d.data}
          zIndex={d.zIndex}
          opacity={d.opacity}
          blur={5}
          radius={3.3}
          fillPalette={d.fillPalette}
          weighted={d.weighted}
          scaleMax={d.scaleMax}
        />
      ));
  }, [map, layers]);

  // Render the base tile layer using self-hosted Protomaps/PMTiles (no API key needed)
  const baseLayer = useMemo(() => {
    if (!map) return null;

    return (
      <ProtomapsLayer
        map={map}
        zIndex={0}
        opacity={1}
        pmtilesUrl={mapSettings?.pmtilesUrl || '/tiles/basemap.pmtiles'}
      />
    );
  }, [map, mapSettings?.pmtilesUrl]);

  const maskLayers = useMemo(() => {
    if (!map || !mapOptions?.shadedMask) return null;
     
    const wktFormat = new WKT();
    const feature = wktFormat.readFeature(mapOptions.shadedMask, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });

    return (
      <MaskLayer
        map={map}
        key="maskLayer"
        source={new VectorSource({
          features: [feature]
        })}
        opacity={1}
        zIndex={1}
        blur={mapOptions.shadedMaskBlur || 14}
        smoothing={mapOptions.shadedMaskSmoothing || 0.4}
      />
    );
  }, [map, mapOptions?.shadedMask, mapOptions?.shadedMaskBlur, mapOptions?.shadedMaskSmoothing]);

  const dtmLayers = useMemo(() => {
    if (!map || !layers || layers.length < 1) return null;

    const dtmLayer = layers.find(layer => layer.type === 'dtm');
    if (!dtmLayer || !dtmLayer.visible) return null;

    return (
      <DtmLayer
        key={dtmLayer.id}
        map={map}
        selectedViewCode={dtmLayer.view?.id || dtmLayer.view}
        selectedPillarKey={selectedPillarKey}
      />
    );
  }, [map, layers, selectedPillarKey]);

  return (
    <>
      {baseLayer}
      {Array.from(layerInstances.values())}
      {heatmapLayers}
      {maskLayers}
      {dtmLayers}
    </>
  );
};

export default React.memo(LayerManager);
